import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import {
  getDepartmentAssetList,
  getDepartmentAssetListExport,
} from "../../services/UserService";
import { getAllDepartments } from "../../services/SettingServices";
import { CancelRequest } from "../../services/AxiosInstance";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { FaDownload } from "react-icons/fa";

const DepartmentAsset = ({ user }) => {
  const userRole = user.role;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportloading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [departmentOption, setDepartmentOption] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [param, setParam] = useState({
    query: "",
    department: user.department_id || "",
    page: 1,
    page_size: 10,
  });

  useEffect(() => {
    if (userRole !== "HOD") {
      getDepartment();
    } else {
      getAssetList();
    }
  }, []);

  const getDepartment = () => {
    getAllDepartments()
      .then((res) => {
        const deptList = res?.data?.results || [];
        const options = deptList.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));
        setDepartmentOption(options);

        if (user.department_id && userRole !== "HOD") {
          const userDept = options.find(
            (opt) => opt.value === user.department_id
          );
          if (userDept) {
            setSelectedDepartment(userDept);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch departments:", err);
        setError("Failed to load department list. Please try again.");
      });
  };

  const getAssetList = () => {
    setLoading(true);

    const departmentParam =
      userRole === "HOD" ? user.department_id : selectedDepartment?.value || "";

    if (!departmentParam && userRole !== "HOD") {
      setLoading(false);
      setData([]);
      setTotal(0);
      return;
    }

    getDepartmentAssetList({
      ...param,
      department: departmentParam,
    })
      .then((res) => {
        setError("");
        setData(res?.data?.results || []);
        setTotal(res?.data?.count || 0);
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load assets. Please try again."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAssetList();
  }, [param]);

  useEffect(() => {
    if (userRole !== "HOD") {
      const newDepartment = selectedDepartment?.value || "";
      if (param.department !== newDepartment) {
        setParam((prev) => ({ ...prev, department: newDepartment, page: 1 }));
      }
    }
  }, [selectedDepartment]);

  const handlePageChange = (page) => {
    setParam((prev) => ({
      ...prev,
      page: page.selected + 1,
    }));
  };

  const handleSearchChange = (value) => {
    if (value.length > 0) CancelRequest();
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  const handleExport = async () => {
    setExportLoading(true);

    try {
      const departmentParam =
        userRole === "HOD"
          ? user.department_id
          : selectedDepartment?.value || "";

      const exportParam = new URLSearchParams({
        ...param,
        export: true,
        department: departmentParam,
      }).toString();

      const response = await getDepartmentAssetListExport(exportParam);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Department-Assets.xlsx`
      );
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Export started successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Export failed. Please try again."
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="px-4 py-3 d-flex justify-content-end gap-2 flex-column flex-md-row">
        <div className="flex-fill flex-md-grow-0">
          <DebounceSmallSearchInput
            handleSearchChange={handleSearchChange}
            placeholder="Search assets..."
          />
        </div>

        <div className="d-flex flex-wrap gap-2">
          {userRole !== "HOD" && (
            <GlobalSearchableDropdown
              options={departmentOption}
              selected={selectedDepartment}
              onSelect={setSelectedDepartment}
              placeholder="Select Department"
              isClearable
            />
          )}

          <button
            className="btn btn-success d-flex align-items-center gap-1 align-self-center"
            style={{ padding: "0.4rem 0.6rem" }}
            onClick={handleExport}
            disabled={
              exportloading || (userRole !== "HOD" && !selectedDepartment)
            }
            title={
              userRole !== "HOD" && !selectedDepartment
                ? "Please select a department first"
                : ""
            }
          >
            {exportloading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FaDownload />
            )}
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <div className="text-center my-4 text-danger">{error}</div>
      ) : userRole !== "HOD" && !selectedDepartment ? (
        <div className="text-center my-4">
          Please select a department to view assets
        </div>
      ) : data.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table fs-6 mb-0 align-middle">
              <thead className="table-light custom-thead">
                <tr>
                  <th>Asset Number</th>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Cost (₹)</th>
                  <th>Allocate By</th>
                  {userRole !== "HOD" && <th>Allocate To</th>}
                  <th>Allocate Date</th>
                </tr>
              </thead>
              <tbody>
                {data.map((asset, index) => (
                  <tr key={index}>
                    <td>
                      <Link
                        to={`/asset/${asset?.asset}/basic-details`}
                        className="fw-bold text-primary"
                      >
                        {asset?.asset_data?.asset_number || "-"}
                      </Link>
                    </td>
                    <td>{asset?.asset_data?.name || "-"}</td>
                    <td>{asset?.asset_data?.brand || "-"}</td>
                    <td>{`₹${asset?.asset_data?.purchase_cost}` || "-"}</td>
                    <td>{asset.allocated_by?.name || "-"}</td>
                    {userRole !== "HOD" && (
                      <td>{asset?.allocated_to?.department || "-"}</td>
                    )}
                    <td>
                      {asset.allocated_at
                        ? format(new Date(asset.allocated_at), "dd MMM yyyy")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4">
            <Pagination
              currentPage={param.page}
              recordsPage={param.page_size}
              dataLength={total}
              handlePagination={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="text-center my-4">
          No assets found{" "}
          {userRole === "HOD"
            ? "in your department"
            : "for selected department"}
        </div>
      )}
    </div>
  );
};

export default DepartmentAsset;
